import { ReactNode, useMemo } from "react"
import { TestData } from "../Data/TestData"
import { MockAppState, MockAppView, MockShipView } from "../Data/App"
import { AppContext } from "../../app/App"
import { ShipContext, ShipContextProps } from "../../app/App/Context"
import { DataStore, DataStoreProvider } from "../../app/Data"
import { IAppState, ICruise, IDay, ILog, IStatus, ITrip } from "../../app/Types"
import { ApiUser } from "../../app/Types/Generated/LogbookApi/ApiRuntime"
import { IAccountUserLevel } from "../../app/Types/Generated/LogbookModel/AccountUser"
import { IShipUserLevel } from "../../app/Types/Generated/LogbookModel/ShipUser"
import { AppHeaderView } from "../../app/Views/AppHeaderView"
import { AppViewContext } from "../../app/Views/AppView"
import { FocusView } from "../../app/Views/FocusView"
import type { FocusViewConfirmationType } from "../../app/Views/Focus/Context"
import { InstrumentsView } from "../../app/Views/InstrumentsView"
import { ShipViewContext } from "../../app/Views/ShipView"
import { logger } from "../../app/App/Logger"

// Full-access `ApiUser` stub mirroring these shared harnesses' previous
// "always true" `requireUserLevel` behavior — fixtures render with the
// highest ship/account levels so every `.available()`-gated affordance
// remains visible by default.
const fullAccessApiUser: ApiUser = {
  shipLevel: (_shipId?: number) => IShipUserLevel.primary,
  accountLevel: (_accountId?: number) => IAccountUserLevel.admin,
};

export interface LogbookContentViewProps {
  children: ReactNode;
  data: TestData;
  language: string;
  cloud?: boolean;
  hideMenus?: boolean;
  hideShipMenu?: boolean;
  overlay?: ReactNode;
  updateAppState?: (state: IAppState) => IAppState;
}

export function LogbookContentView({ children, cloud, data, language, hideMenus, hideShipMenu, overlay, updateAppState }: LogbookContentViewProps) {
  const shipContext: ShipContextProps = {
    ship: data.ship,
    requireUserLevel: (_level: string) => true,
    apiUser: fullAccessApiUser,
  };
  const initAppState = MockAppState(data, language, cloud || false);
  const appState = updateAppState ? updateAppState(initAppState) : initAppState;
  // Offline, seed-only store so flag-on data-model variants have a
  // store to read in these mock harnesses (catalog/demo/print), which bypass the
  // app root's DataStoreProvider. Seeded from the same `TestData` the legacy variant
  // reads via its `init*` props, so both flag states render identical data. No
  // socket/apiClient -> nothing is fetched or subscribed; it's purely the seed.
  const dataStore = useMemo(
    () => new DataStore({
      seed: {
        Trip: data.trips ?? [],
        Day: data.days ?? [],
        Log: data.logs ?? [],
        Status: data.statuses ?? [],
        Track: data.tracks ?? [],
        Note: data.notes ?? [],
        NoteAttachment: data.attachments ?? [],
      },
    }),
    [data],
  );
  return (
    <div className="LogbookContentView">
      <AppContext.Provider value={appState}>
        <div className="RootView">
          <AppViewContext.Provider value={MockAppView(data)}>
            <div className="AppView">
              <ShipContext.Provider value={shipContext}>
                <AppHeaderView ship={data.ship} hideMenus={hideMenus} hideShipMenu={hideShipMenu} />
                <DataStoreProvider store={dataStore}>
                  {children}
                  {overlay}
                </DataStoreProvider>
              </ShipContext.Provider>
            </div>
          </AppViewContext.Provider>
        </div>
      </AppContext.Provider>
    </div>
  );
}

export interface LogbookContentShipViewProps {
  children: ReactNode;
  cloud?: boolean;
  data: TestData;
  language: string;
  focusActive?: boolean;
  focusInitConfirmationType?: "trip" | "cruise";
  focusInitCruise?: ICruise;
  focusInitTrip?: ITrip;
  focusInitDay?: IDay;
  focusInitDayTomorrow?: IDay;
  focusInitDayTomorrowDetails?: boolean;
  focusInitLogs?: ILog[];
  focusInitLogHighlight?: number;
  focusInitLogTabs?: Record<number, string>;
  focusInitCurrentLog?: ILog;
  focusInitStatuses?: IStatus[];
  focusInitUserAdvice?: string;
  focusInitUserAdviceData?: unknown;
  instruments?: boolean;
  overlay?: ReactNode;
  updateAppState?: (state: IAppState) => IAppState;
}

export function LogbookContentShipView({ children, cloud, data, language, focusActive, focusInitConfirmationType, focusInitCruise, focusInitTrip, focusInitDay, focusInitDayTomorrow, focusInitDayTomorrowDetails, focusInitLogs, focusInitLogHighlight, focusInitLogTabs, focusInitCurrentLog, focusInitStatuses, focusInitUserAdvice, focusInitUserAdviceData, instruments, overlay, updateAppState }: LogbookContentShipViewProps) {
  logger.debug("=== LOGBOOK_CONTENT_SHIP_VIEW ===", { focusInitLogs, focusInitLogHighlight, focusInitLogTabs, focusInitCurrentLog, focusInitStatuses, focusInitUserAdvice, focusInitUserAdviceData });
  const shipContext: ShipContextProps = {
    ship: data.ship,
    requireUserLevel: (_level: string) => true,
    apiUser: fullAccessApiUser,
  };
  return (
    <LogbookContentView cloud={cloud} data={data} language={language} overlay={overlay} updateAppState={updateAppState}>
      <ShipViewContext.Provider value={MockShipView(data, focusActive)}>
        <ShipContext.Provider value={shipContext}>
          <div className="ShipView">
            <div className="ShipView-content">
              <div className="ShipContentView">
                {focusActive ? <FocusView show={true} initConfirmationType={focusInitConfirmationType as FocusViewConfirmationType | undefined} initCruise={focusInitCruise} initTrip={focusInitTrip} initDay={focusInitDay} initDayTomorrow={focusInitDayTomorrow} initDayTomorrowDetails={focusInitDayTomorrowDetails} initLogs={focusInitLogs} initLogHighlight={focusInitLogHighlight} initLogTabs={focusInitLogTabs} initCurrentLog={focusInitCurrentLog} initStatuses={focusInitStatuses} initUserAdvice={focusInitUserAdvice} initUserAdviceData={focusInitUserAdviceData} /> : null}
                {!focusActive && children}
              </div>
            </div>
            {instruments && <InstrumentsView minimized={true} setMinimized={() => undefined} />}
          </div>
        </ShipContext.Provider>
      </ShipViewContext.Provider>
    </LogbookContentView>
  );
}
